import { MobXProviderContext } from "mobx-react";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import Unauthorized from "../../../shared/pages/Unauthorized";
import {
  appInnerCardStyle,
  appMutedTextStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import ProviderSubscriptionDialog from "../../../components/general/ProviderSubscriptionDialog";
import { canAccessCaregiverRole } from "../utils/caregiverRoleAccess";

const normalizeSearchText = (value: any) => String(value || "").trim().toLowerCase();

type CaregiverDetailsPageProps = {
  sharedMode?: boolean;
};

export const CaregiverDetailsPageContent = ({ sharedMode = false }: CaregiverDetailsPageProps) => {
  const { residentId } = useParams();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  const mobile = useResponsive().isMobile;
  const navigate = useNavigate();

  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.isAdmin();
  const canUseCaregiverWorkspace = sharedMode
    ? Boolean(userRoleType) || isAdminUser
    : canAccessCaregiverRole(authStore);
  const [loading, setLoading] = useState<boolean>(false);
  const [carePlanData, setCarePlanData] = useState<any>(null);
  const [isProviderSubscribed, setIsProviderSubscribed] = useState<boolean>(true);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState<boolean>(false);
  const [showMissingCarePlanDialog, setShowMissingCarePlanDialog] = useState<boolean>(false);
  const [accessBlockedMessage, setAccessBlockedMessage] = useState<string>("");
  const [selectedSection, setSelectedSection] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);

  useEffect(() => {
    const loadCaregiverResidentCarePlan = async () => {
      if (!residentId) {
        return;
      }

      setLoading(true);
      try {
        await backendAPIStore.init();
        if (userRoleType === ROLE_TYPE_PROVIDER && !isAdminUser) {
          const subscriptionDetails = await backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_PROVIDER);
          const subscribed = !!subscriptionDetails?.isSubscribed;
          setIsProviderSubscribed(subscribed);
          if (!subscribed) {
            setShowSubscriptionDialog(true);
            setCarePlanData(null);
            return;
          }
        }
        const response = await backendAPIStore.getCaregiverResidentCarePlan(residentId);
        const responseRows = Array.isArray(response?.rows) ? response.rows : [];
        const hasBehaviorRows = responseRows.some((row: any) => row?.sourceType === "behavior");
        setCarePlanData(response || null);
        setAccessBlockedMessage("");
        setShowMissingCarePlanDialog(
          userRoleType === ROLE_TYPE_PROVIDER
          && !hasBehaviorRows,
        );
      } catch (error: any) {
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setIsProviderSubscribed(false);
          setShowSubscriptionDialog(true);
          setCarePlanData(null);
          setAccessBlockedMessage("Search Care Plan is unavailable because the provider subscription is inactive.");
        } else {
          toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: error?.message || "Unable to load resident care plan.",
            life: 4000,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    void loadCaregiverResidentCarePlan();
  }, [backendAPIStore, residentId]);

  const allRows = Array.isArray(carePlanData?.rows) ? carePlanData.rows : [];
  const hasBehaviorRows = allRows.some((row: any) => row?.sourceType === "behavior");
  const canOpenManageCarePlan = userRoleType === ROLE_TYPE_PROVIDER && Boolean(carePlanData?.homeId) && Boolean(residentId);
  const sectionOptions = useMemo(
    () => ((Array.isArray(carePlanData?.sections) ? carePlanData.sections : []).map((sectionLabel: string) => ({
      label: sectionLabel,
      value: sectionLabel,
    }))),
    [carePlanData],
  );

  const hasActiveQuery = Boolean(normalizeSearchText(searchTerm) || selectedSection);

  const filteredRows = useMemo(() => {
    const normalizedSearch = normalizeSearchText(searchTerm);
    return allRows.filter((row: any) => {
      if (selectedSection && row?.sectionLabel !== selectedSection) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = [
        row?.sectionLabel,
        row?.itemLabel,
        row?.description,
        row?.instructions,
      ].map(normalizeSearchText).join(" ");

      return haystack.includes(normalizedSearch);
    });
  }, [allRows, searchTerm, selectedSection]);

  const importantRows = useMemo(
    () => filteredRows.filter((row: any) => normalizeSearchText(row?.instructions || row?.description)),
    [filteredRows],
  );

  const completeSearch = (event: any) => {
    const query = normalizeSearchText(event.query || "");
    if (!query) {
      setSearchSuggestions([]);
      return;
    }

    const suggestions = allRows
      .filter((row: any) => {
        if (selectedSection && row?.sectionLabel !== selectedSection) {
          return false;
        }
        const haystack = [
          row?.sectionLabel,
          row?.itemLabel,
          row?.description,
          row?.instructions,
        ].map(normalizeSearchText).join(" ");
        return haystack.includes(query);
      })
      .slice(0, 8)
      .map((row: any) => ({
        label: `${row?.itemLabel || "Item"} - ${row?.sectionLabel || "Section"}`,
        value: row?.itemLabel || "",
      }));

    setSearchSuggestions(suggestions);
  };

  if (userRoleType && userRoleType !== ROLE_TYPE_PROVIDER && !canUseCaregiverWorkspace) {
    return <Unauthorized />;
  }

  const instructionBodyTemplate = (row: any) => (
    <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, color: "#7c2d12" }}>
      {row?.instructions || "No caregiver instructions documented."}
    </div>
  );

  const residentNotesBodyTemplate = (row: any) => (
    <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, color: "#334155" }}>
      {row?.description || "No resident notes documented."}
    </div>
  );

  const sectionTagTemplate = (row: any) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "0.28rem 0.7rem",
        background: "#eff6ff",
        color: "#1d4ed8",
        fontSize: "0.78rem",
        fontWeight: 700,
      }}
    >
      {row?.sectionLabel || "Section"}
    </span>
  );

  const itemTagTemplate = (row: any) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "0.28rem 0.7rem",
        background: "#fff7ed",
        color: "#9a3412",
        fontSize: "0.78rem",
        fontWeight: 700,
      }}
    >
      {row?.itemLabel || "Item"}
    </span>
  );

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <ProviderSubscriptionDialog
        visible={showSubscriptionDialog}
        featureName="Search Care Plan"
        message="Search Care Plan is unavailable because the provider subscription is inactive."
        showBillingButton={userRoleType === ROLE_TYPE_PROVIDER}
        onHide={() => setShowSubscriptionDialog(false)}
      />
      <Dialog
        visible={showMissingCarePlanDialog}
        onHide={() => setShowMissingCarePlanDialog(false)}
        header="Manage Care Plan First"
        style={{ width: mobile ? "96vw" : "34rem" }}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button
              type="button"
              label="Close"
              rounded
              outlined
              severity="secondary"
              onClick={() => setShowMissingCarePlanDialog(false)}
            />
            {canOpenManageCarePlan ? (
              <Button
                type="button"
                label="Go to Manage Care Plan"
                rounded
                severity="warning"
                onClick={() => {
                  setShowMissingCarePlanDialog(false);
                  navigate(RouterAddress.getResidentNCPPagePath(String(carePlanData?.homeId || ""), String(residentId || "")));
                }}
              />
            ) : null}
          </div>
        )}
      >
        <div style={{ color: "#475569", lineHeight: 1.7 }}>
          Upload the assessment first, then create a care plan in Manage Care Plan before using Search Care Plan.
        </div>
      </Dialog>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div>
          {renderSurfaceHeader(
            `${carePlanData?.residentName || "Resident"} - Search Care Plan`,
            "Search the care plan first, then review matching instructions and notes in a clean table.",
            "pi pi-book",
          )}
        </div>

        {loading ? (
          <div className="card flex justify-content-center p-5">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : (((userRoleType === ROLE_TYPE_PROVIDER && !isAdminUser && !isProviderSubscribed)) || accessBlockedMessage) ? (
          <div style={{ ...appInnerCardStyle, maxWidth: 760, margin: "0 auto", padding: "1rem 1.1rem" }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Subscription required</div>
            <div className="mt-2" style={appMutedTextStyle}>
              {accessBlockedMessage || "Search Care Plan is available for subscribed providers only."}
            </div>
          </div>
        ) : (
          <div className="flex flex-column gap-4">
            <div
              style={{
                minHeight: hasActiveQuery ? undefined : "62vh",
                display: "flex",
                alignItems: hasActiveQuery ? "stretch" : "center",
                justifyContent: hasActiveQuery ? "flex-start" : "center",
                padding: "1rem 0",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: hasActiveQuery && !mobile ? 1040 : 920,
                  margin: "0 auto",
                  textAlign: hasActiveQuery ? "left" : "center",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.1rem",
                }}
              >
                {!hasActiveQuery ? (
                  <div>
                    <div style={{ fontSize: "2.3rem", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.03em" }}>
                      Search the care plan
                    </div>
                    <div className="mt-3" style={{ ...appMutedTextStyle, fontSize: "1rem", lineHeight: 1.7 }}>
                      Find care instructions, behavior guidance, and resident notes fast without scanning the full plan.
                    </div>
                  </div>
                ) : null}

                <div
                  style={{
                    ...appInnerCardStyle,
                    padding: "1.3rem",
                    borderRadius: 28,
                    boxShadow: !hasActiveQuery ? "0 24px 70px rgba(15, 23, 42, 0.08)" : undefined,
                  }}
                >
                  <div className="grid">
                    <div className="col-12 lg:col-4">
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.55rem" }}>Section</div>
                        <Dropdown
                          value={selectedSection}
                          onChange={(e) => {
                            setSelectedSection(e.value || "");
                            setSearchSuggestions([]);
                          }}
                          options={sectionOptions}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Choose a section"
                          showClear
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="col-12 lg:col-8">
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.55rem" }}>
                          {hasActiveQuery ? "Quick Search" : "Search"}
                        </div>
                        <AutoComplete
                          value={searchTerm}
                          suggestions={searchSuggestions}
                          completeMethod={completeSearch}
                          field="label"
                          dropdown
                          className="w-full"
                          inputClassName="w-full"
                          placeholder="Search section, item, instructions, or notes"
                          onChange={(e) => setSearchTerm(typeof e.value === "string" ? e.value : String(e.value?.value || ""))}
                          onSelect={(e) => setSearchTerm(String(e.value?.value || ""))}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {hasActiveQuery ? (
                  importantRows.length === 0 ? (
                    <div style={appInnerCardStyle}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>No matching care plan items</div>
                      <div className="mt-2" style={appMutedTextStyle}>
                        Try another section or search term.
                      </div>
                    </div>
                  ) : (
                    mobile ? (
                      <div className="flex flex-column gap-3">
                        {importantRows.map((row: any, index: number) => (
                          <div key={`${row?.sectionKey}-${row?.itemKey}-${index}`} style={appInnerCardStyle}>
                            <div className="flex flex-wrap gap-2 align-items-center">
                              {sectionTagTemplate(row)}
                              {itemTagTemplate(row)}
                            </div>
                            <div className="mt-3">
                              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Resident Notes</div>
                              {residentNotesBodyTemplate(row)}
                            </div>
                            <div className="mt-3" style={{ paddingTop: "0.85rem", borderTop: "1px solid #e2e8f0" }}>
                              <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Instructions</div>
                              {instructionBodyTemplate(row)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        style={{
                          ...appInnerCardStyle,
                          padding: "0.4rem",
                          overflow: "hidden",
                          width: "100%",
                          margin: "0 auto",
                        }}
                      >
                        <DataTable
                          value={importantRows}
                          paginator={importantRows.length > 12}
                          rows={12}
                          rowsPerPageOptions={[12, 24, 48]}
                          stripedRows
                          emptyMessage="No matching care plan items"
                        >
                          <Column header="Section" body={sectionTagTemplate} sortable field="sectionLabel" style={{ width: "14rem", verticalAlign: "top" }} />
                          <Column header="Item" body={itemTagTemplate} sortable field="itemLabel" style={{ width: "14rem", verticalAlign: "top" }} />
                          <Column header="Resident Notes" body={residentNotesBodyTemplate} style={{ minWidth: "20rem", verticalAlign: "top" }} />
                          <Column header="Instructions" body={instructionBodyTemplate} style={{ minWidth: "24rem", verticalAlign: "top" }} />
                        </DataTable>
                      </div>
                    )
                  )
                ) : (!hasBehaviorRows && userRoleType === ROLE_TYPE_PROVIDER) ? (
                  <div style={appInnerCardStyle}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>No behavior care plan available yet</div>
                    <div className="mt-2" style={appMutedTextStyle}>
                      Upload the assessment first, then create a care plan with behaviors in Manage Care Plan before searching here.
                    </div>
                    {canOpenManageCarePlan ? (
                      <div className="mt-3">
                        <Button
                          type="button"
                          label="Go to Manage Care Plan"
                          rounded
                          severity="warning"
                          onClick={() => navigate(RouterAddress.getResidentNCPPagePath(String(carePlanData?.homeId || ""), String(residentId || "")))}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CaregiverDetailsPage = () => <CaregiverDetailsPageContent />;

export default CaregiverDetailsPage;
